// src/oura/merchant/useMerchantDrawers.ts
//
// The contract every merchant screen codes against to open a drawer, apart
// from the provider that fulfils it (MerchantDrawers.tsx).
//
// One queue: every question and document that needs the merchant, most
// pressing first (`needsYouQueue`, docAsks.ts). "Start answering" on Home,
// Decisions and Work walks it with `openQueue`, one item per view with Next.
// A row opens its own item (`openQuestion`, `openDoc`); an item in the queue
// still walks on through it from there.
//
// The documents are read here once for every screen (useMerchantDocuments),
// so the queue, the Decisions sentence, Home and the nav badge count the
// same list. The unsent answers are counted here too, so a row can say
// "Ready to send".

import { createContext, useContext } from "react";
import type { MerchantDocs } from "../hooks/useMerchantDocuments";
import type { QueueEntry } from "./docAsks";

export interface MerchantDrawers {
    /** One question, open or not. In the queue, it walks on through the queue. */
    openQuestion(itemId: string): void;
    /** One shared document. In the queue, it walks on through the queue. */
    openDoc(docId: string): void;
    /**
     * Walk everything that needs them, from `startId` (a question or a doc id)
     * when it is in the queue, else from the top. With nothing waiting, a
     * short note says so.
     */
    openQueue(startId?: string): void;
    /** One report: what is inside it, and the viewer. */
    openReport(reportId: string): void;
    /** Open questions with an answer ready and not sent yet. */
    draftCount: number;
    /** The same questions by item id, so a row can say "Ready to send". */
    readyDraftIds: ReadonlySet<string>;
    /** Everything that needs them now, most pressing first. */
    queue: QueueEntry[];
    /** The shared documents, kept fresh: one read for every screen. */
    docs: MerchantDocs;
}

/** Set by `MerchantDrawersProvider`, which only ClientPage mounts. */
export const MerchantDrawersContext = createContext<MerchantDrawers | null>(null);

/** The merchant drawers. Throws outside `MerchantDrawersProvider`. */
export function useMerchantDrawers(): MerchantDrawers {
    const drawers = useContext(MerchantDrawersContext);
    if (!drawers) throw new Error("useMerchantDrawers must be used inside MerchantDrawersProvider");
    return drawers;
}
